/**
 * Receptor residente en el extranjero (RFC genérico `XEXX010101000`): países que se
 * ofrecen para su residencia fiscal (c_Pais) y el formato de su número de registro
 * de identidad fiscal (NumRegIdTrib) por país. Los dos datos son opcionales sin el
 * complemento de Comercio Exterior, pero si viene el número el SAT exige el país.
 */
export const COUNTRY_CODES = [
  { code: 'USA', name: 'Estados Unidos' },
  { code: 'CAN', name: 'Canadá' },
  { code: 'ESP', name: 'España' },
  { code: 'DEU', name: 'Alemania' },
  { code: 'FRA', name: 'Francia' },
  { code: 'GBR', name: 'Reino Unido' },
  { code: 'ITA', name: 'Italia' },
  { code: 'NLD', name: 'Países Bajos' },
  { code: 'CHE', name: 'Suiza' },
  { code: 'BRA', name: 'Brasil' },
  { code: 'ARG', name: 'Argentina' },
  { code: 'COL', name: 'Colombia' },
  { code: 'CHL', name: 'Chile' },
  { code: 'PER', name: 'Perú' },
  { code: 'JPN', name: 'Japón' },
  { code: 'CHN', name: 'China' },
  { code: 'KOR', name: 'Corea del Sur' },
  { code: 'IND', name: 'India' },
  { code: 'AUS', name: 'Australia' },
];

export interface CountryTaxIdRule {
  pattern: RegExp;
  placeholder: string;
  hint: string;
  errorMessage: string;
}

const DEFAULT_TAX_ID_RULE: CountryTaxIdRule = {
  pattern: /^[^|]{1,40}$/,
  placeholder: 'Identificador tributario del extranjero',
  hint: 'Máximo 40 caracteres. No se permite el carácter "|".',
  errorMessage: 'La clave no debe exceder 40 caracteres ni contener "|".',
};

const COUNTRY_TAX_ID_RULES: Record<string, CountryTaxIdRule> = {
  USA: {
    pattern: /^[0-9]{9}$/,
    placeholder: 'Ej. 123456789',
    hint: 'SSN/EIN — exactamente 9 dígitos, sin guiones ni espacios.',
    errorMessage: 'Para Estados Unidos debe ser exactamente 9 dígitos, sin guiones.',
  },
  CAN: {
    pattern: /^[A-Z0-9]{9}$/,
    placeholder: 'Ej. 123456789',
    hint: 'Business Number o SIN — 9 caracteres alfanuméricos en mayúsculas.',
    errorMessage: 'Para Canadá deben ser 9 caracteres alfanuméricos en mayúsculas.',
  },
  ESP: {
    pattern: /^[A-Z0-9]{8,9}$/,
    placeholder: 'Ej. B12345674',
    hint: 'NIF / NIE / CIF — 8 o 9 caracteres alfanuméricos.',
    errorMessage: 'Para España deben ser 8 o 9 caracteres alfanuméricos.',
  },
  GBR: {
    pattern: /^[0-9]{9,12}$/,
    placeholder: 'Ej. 123456789',
    hint: 'VAT (9 o 12 dígitos) o UTR (10 dígitos), sin espacios.',
    errorMessage: 'Para Reino Unido deben ser entre 9 y 12 dígitos, sin espacios.',
  },
};

export const getCountryTaxIdRule = (countryCode: string): CountryTaxIdRule =>
  COUNTRY_TAX_ID_RULES[countryCode] || DEFAULT_TAX_ID_RULE;

export const normalizeForeignTaxId = (value: string | undefined): string =>
  (value ?? '').trim().toUpperCase();

/**
 * `null` si el país y el número se pueden timbrar; si no, el mensaje de qué corregir.
 * Sin número no hay nada que validar, porque los dos son opcionales. Es la misma regla
 * que aplica el paso de pago del flujo legacy.
 */
export const foreignTaxIdProblem = (
  countryCode: string | undefined,
  numRegIdTrib: string | undefined,
): string | null => {
  const normalized = normalizeForeignTaxId(numRegIdTrib);
  if (!normalized) return null;
  if (!countryCode) return 'Elige el país de tu cliente: el SAT lo pide si capturas su número fiscal.';
  const rule = getCountryTaxIdRule(countryCode);
  return rule.pattern.test(normalized) ? null : rule.errorMessage;
};

/** El nombre del país para mostrarlo; un código fuera de la lista se muestra tal cual. */
export const countryNameOf = (code: string): string =>
  COUNTRY_CODES.find((country) => country.code === code)?.name ?? code;
